import type { ConnectedContentProductDisplayModel } from 'autogen/swagger/all';
import { ThemeProvider, css } from 'styled-components';
import { stringToKey } from 'modules/helpers/strings';
import { type DesignTheme, getTheme } from 'helpers/designThemes';
import DynamicItemCarousel from 'components/generic/ScrollSnapSlider/components/DynamicItemCarousel';
import { breakpoint, sizes } from 'variables';
import Card from './card';
import { Spacing16 } from 'autogen/design-tokens/tokens';
import ConnectedContentSurveyItem from 'components/surveySystem/components/ConnectedContentSurveyItem';
import type { ColorTheme } from 'types/tokens';
import usePageContext from 'hooks/user/usePageContext';

const scrollContainerCss = css`
    && {
        padding: 0 ${Spacing16};
        margin: 0 -${Spacing16};
        scroll-padding-inline-end: ${Spacing16};
        scroll-padding-inline-start: ${Spacing16};

        ${breakpoint.up(sizes.xl)} {
            padding: 0;
            margin: 0;
        }
    }
`;

export interface IProductDisplayConnectedContent {
    model: ConnectedContentProductDisplayModel[];
}

function listTheme(theme: DesignTheme, i: number) {
    const { color3, color4 } = theme;

    switch ((i + 1) % 3) {
        case 1:
            return { ...theme, color1: color3 };
        case 2:
            return { ...theme, color1: color4 };
        case 0:
        default:
            return theme;
    }
}

function ProductDisplayConnectedContent(props: IProductDisplayConnectedContent) {
    const { model } = props;

    const { designThemeName } = usePageContext();

    const globalTheme = getTheme((designThemeName || 'NordicGrey') as ColorTheme);

    return (
        <DynamicItemCarousel
            gapSize={Spacing16}
            items={model}
            scrollContainerCss={scrollContainerCss}
            itemRenderer={(item, index) => {
                const { heading, layoutType } = item;
                const keySuffix = heading ? stringToKey(heading) : index;

                if (item.surveyQuestion) {
                    return (
                        <ConnectedContentSurveyItem
                            key={`${layoutType}_${keySuffix}`}
                            model={item}
                            type="ConnectedContentProductDisplay"
                        />
                    );
                }

                return (
                    <ThemeProvider theme={listTheme(globalTheme, index)} key={`${layoutType}_${keySuffix}`}>
                        <Card card={item} trackingIndex={index + 1} />
                    </ThemeProvider>
                );
            }}
        />
    );
}

export default ProductDisplayConnectedContent;
